import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartLineUp, TrendUp, TrendDown } from "@phosphor-icons/react";
import GaugeMeter from "../components/GaugeMeter";
import SentimentBreakdown from "../components/SentimentBreakdown";
import SEO from "../components/SEO";
import TradingViewWidget from "../components/TradingViewWidget";
import NewsFeed from "../components/NewsFeed";
import SentimentBacktestChart from "../components/SentimentBacktestChart";
import Sparkline from "../components/Sparkline";
import ForecastCard from "../components/ForecastCard";
import SymbolSearch from "../components/SymbolSearch";
import OptionsPredictor from "../components/OptionsPredictor";
import { fetchAsset, fetchAssetHistory, fetchAssetNews, fetchForecast } from "../lib/api";
import { toTradingViewSymbol, toTradingViewInterval } from "../lib/symbols";
import { formatPrice, currencyCode } from "../lib/currency";

const RANGES = [
  { id: 30, label: "1M" },
  { id: 90, label: "3M" },
  { id: 180, label: "6M" },
  { id: 365, label: "1Y" },
];

export default function AssetDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [tickerNews, setTickerNews] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [days, setDays] = useState(180);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchAsset(symbol)
      .then((a) => !cancelled && setData(a))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    fetchAssetHistory(symbol, days)
      .then((h) => !cancelled && setHistory(h))
      .catch(() => !cancelled && setHistory(null));
    return () => { cancelled = true; };
  }, [symbol, days]);

  useEffect(() => {
    let cancelled = false;
    setTickerNews(null);
    fetchAssetNews(symbol, 10)
      .then((n) => !cancelled && setTickerNews(n))
      .catch(() => !cancelled && setTickerNews(null));
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    setForecast(null);
    setForecastLoading(true);
    fetchForecast(symbol)
      .then((f) => !cancelled && setForecast(f))
      .catch(() => !cancelled && setForecast(null))
      .finally(() => !cancelled && setForecastLoading(false));
    return () => { cancelled = true; };
  }, [symbol]);

  const composite = data?.composite || 0;
  const label = data?.label || "Loading";
  const quote = data?.quote;
  const change = quote?.change_pct || 0;
  const up = change >= 0;

  const sentimentSeries = useMemo(
    () => (history?.series || []).map((s) => s.sentiment),
    [history]
  );
  const stats = useMemo(() => {
    const series = history?.series || [];
    if (series.length === 0) return null;
    const sentiments = series.map((s) => s.sentiment);
    const prices = series.map((s) => s.price);
    const startSent = sentiments[0];
    const endSent = sentiments[sentiments.length - 1];
    const startPrice = prices[0];
    const endPrice = prices[prices.length - 1];
    const priceReturn = ((endPrice - startPrice) / startPrice) * 100;
    const avgSent = sentiments.reduce((a, b) => a + b, 0) / sentiments.length;
    // crude correlation with next-day return
    const returns = series.slice(1).map((s, i) => (s.price - series[i].price) / series[i].price);
    const sentLeading = sentiments.slice(0, -1);
    const meanS = sentLeading.reduce((a, b) => a + b, 0) / sentLeading.length;
    const meanR = returns.reduce((a, b) => a + b, 0) / returns.length;
    let num = 0, ds = 0, dr = 0;
    for (let i = 0; i < returns.length; i++) {
      const xs = sentLeading[i] - meanS;
      const xr = returns[i] - meanR;
      num += xs * xr;
      ds += xs * xs;
      dr += xr * xr;
    }
    const corr = ds && dr ? num / Math.sqrt(ds * dr) : 0;
    return {
      avgSent,
      priceReturn,
      sentChange: endSent - startSent,
      corr,
    };
  }, [history]);

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="asset-detail-page">
      <SEO
        title={data?.asset?.name ? `${data.asset.name} (${symbol}) Sentiment, Forecast & News` : `${symbol} Sentiment & Forecast`}
        path={`/asset/${encodeURIComponent(symbol)}`}
        description={
          data?.asset?.name
            ? `Live AI sentiment score, analyst price targets and ticker-specific news for ${data.asset.name} (${symbol}). 180-day backtest, technical breakdown (RSI, MACD, MA) and TradingView chart.`
            : `Live AI sentiment, analyst targets and news for ${symbol}. Backtest chart, technical breakdown and live TradingView chart.`
        }
        keywords={[
          `${symbol} sentiment`,
          data?.asset?.name ? `${data.asset.name} stock` : null,
          data?.asset?.name ? `${data.asset.name} price target` : null,
          data?.asset?.name ? `${data.asset.name} news` : null,
          `${symbol} forecast`,
          `${symbol} chart`,
        ].filter(Boolean)}
        image={`https://sent-i-meter.com/api/og/asset/${encodeURIComponent(symbol)}.png`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FinancialProduct",
          name: data?.asset?.name || symbol,
          identifier: symbol,
          category: data?.asset?.asset_class || data?.asset?.region || "Equity",
          url: `https://sent-i-meter.com/asset/${encodeURIComponent(symbol)}`,
        }}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-150"
            data-testid="asset-back"
          >
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="w-8 h-8 bg-white text-black flex items-center justify-center hover:bg-white/90 transition-colors duration-150"
              data-testid="asset-home"
            >
              <ChartLineUp size={16} weight="bold" />
            </button>
            <div className="hidden sm:block">
              <div className="heading text-sm font-bold tracking-tight leading-none" data-testid="asset-symbol">
                {data?.asset?.name || symbol}
              </div>
              <div className="overline mt-0.5 leading-none">{symbol} · {data?.asset?.region || "—"}</div>
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-md hidden md:block">
          <SymbolSearch
            onAdd={(s) => navigate(`/asset/${encodeURIComponent(s)}`)}
            onSelect={(s) => navigate(`/asset/${encodeURIComponent(s)}`)}
            placeholder="Jump to any ticker..."
            testid="asset-search"
          />
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {quote && (
            <div className="flex items-center gap-3" data-testid="asset-quote">
              <span className="mono text-base" data-testid="asset-price">
                {formatPrice(quote.price, {
                  symbol,
                  region: data?.asset?.region,
                  assetClass: data?.asset?.asset_class,
                })}
              </span>
              <span
                className="mono text-[10px] text-white/40"
                data-testid="asset-currency"
              >
                {currencyCode({
                  symbol,
                  region: data?.asset?.region,
                  assetClass: data?.asset?.asset_class,
                })}
              </span>
              <span className={`mono text-xs ${up ? "text-bullish" : "text-bearish"}`}>
                {up ? "▲" : "▼"} {Math.abs(change).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
        {err && (
          <div className="bg-bearish/10 border border-bearish/40 p-4 mb-6 mono text-sm" data-testid="asset-error">
            {err}
          </div>
        )}

        {/* Hero row */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="bg-surface border border-white/10 p-5 grid-bg">
            <div className="flex items-center justify-between mb-2">
              <h2 className="heading text-xl font-bold">Composite Sentiment</h2>
              <span className="overline">live</span>
            </div>
            {loading && !data ? (
              <div className="h-[260px] flex items-center justify-center overline">loading...</div>
            ) : (
              <GaugeMeter value={composite} label={label} />
            )}
            {sentimentSeries.length > 1 && (
              <div className="mt-2 border-t border-white/10 pt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">trend ({days}d)</span>
                  <span className="mono text-xs" style={{ color: stats?.sentChange >= 0 ? "#34C759" : "#FF3B30" }}>
                    {stats?.sentChange >= 0 ? "+" : ""}{stats?.sentChange?.toFixed(1)}
                  </span>
                </div>
                <Sparkline values={sentimentSeries} width={280} height={36} />
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <SentimentBreakdown breakdown={data?.components} />
          </div>

          <div className="bg-surface border border-white/10 p-5">
            <h3 className="heading text-lg font-semibold mb-3">Backtest Stats</h3>
            {stats ? (
              <div className="space-y-3" data-testid="backtest-stats">
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                  <span className="overline">avg sentiment ({days}d)</span>
                  <span className="mono text-base" style={{ color: stats.avgSent < 30 ? "#FF3B30" : stats.avgSent < 70 ? "#FFCC00" : "#34C759" }}>
                    {Math.round(stats.avgSent)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                  <span className="overline">price return ({days}d)</span>
                  <span className={`mono text-base ${stats.priceReturn >= 0 ? "text-bullish" : "text-bearish"}`}>
                    {stats.priceReturn >= 0 ? "+" : ""}{stats.priceReturn.toFixed(2)}%
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                  <span className="overline">sentiment Δ</span>
                  <span className={`mono text-base ${stats.sentChange >= 0 ? "text-bullish" : "text-bearish"}`}>
                    {stats.sentChange >= 0 ? "+" : ""}{stats.sentChange.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="overline">sent → next-day corr</span>
                  <span className="mono text-base flex items-center gap-1">
                    {stats.corr >= 0 ? <TrendUp size={14} weight="bold" color="#34C759" /> : <TrendDown size={14} weight="bold" color="#FF3B30" />}
                    {stats.corr.toFixed(3)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="overline py-6 text-center">no historical data</div>
            )}
            <div className="overline mt-4 text-white/40">
              backtest = technical sentiment vs realised price; correlation is sentiment(t) vs return(t→t+1).
            </div>
          </div>
        </section>

        {/* Nifty Options Predictor — only for the NIFTY 50 index */}
        {symbol === "^NSEI" && <OptionsPredictor />}

        {/* Forecast & Analyst Targets */}
        <section className="mb-6" data-testid="forecast-section">
          <ForecastCard data={forecast} loading={forecastLoading} />
        </section>

        {/* Backtest chart */}
        <section className="bg-surface border border-white/10 mb-6" data-testid="backtest-section">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <h3 className="heading text-lg font-semibold">Sentiment vs Price · Backtest</h3>
            <div className="flex items-center gap-1">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setDays(r.id)}
                  data-testid={`range-${r.label}`}
                  className={`px-2.5 py-1 mono text-xs uppercase tracking-[0.18em] border transition-colors duration-150 ${
                    days === r.id
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="p-3 md:p-5">
            <SentimentBacktestChart series={history?.series || []} height={340} />
          </div>
        </section>

        {/* Chart + News */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="lg:col-span-2">
            <TradingViewWidget symbol={toTradingViewSymbol(symbol)} interval={toTradingViewInterval(symbol)} height={460} />
          </div>
          <div className="lg:col-span-1">
            <NewsFeed items={tickerNews?.items || []} summary={tickerNews?.summary} />
          </div>
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white transition-colors duration-150" data-testid="footer-back">
            ← back to dashboard
          </button>
          <span className="overline">data: yfinance · google news · claude sonnet 4.5</span>
        </footer>
      </main>
    </div>
  );
}
